// src/pages/Campaigns/VolunteerManagement/EventVolunteers.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Card, CardContent,
  Grid, IconButton, Button, Stack
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';
import {
  AvailabilityStatus,
  AvailabilityStatusLabels,
  PreferredTimeSlotLabels,
  EventTypeLabels
} from './volunteerEnums';

const EventVolunteers = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event_id');
  
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [volunteers, setVolunteers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (eventId && chatbotId) {
      loadEventVolunteers();
    }
  }, [eventId, chatbotId]);

  const loadEventVolunteers = async () => {
    setLoading(true);
    try {
      // Load event details first
      const eventResponse = await axiosInstance.get(`/api/events`, {
        params: { chatbot_id: chatbotId }
      });
      const foundEvent = eventResponse.data.data.find(e => e.id === parseInt(eventId));
      setEvent(foundEvent);

      // Load event volunteers
      const volunteersResponse = await axiosInstance.get(`/api/events/volunteers`, {
        params: {
          chatbot_id: chatbotId,
          event_id: eventId
        }
      });
      
      setVolunteers(volunteersResponse.data.data);
      setStatistics(volunteersResponse.data.statistics);
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading volunteers');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      AVAILABLE: 'success',
      NOT_AVAILABLE: 'error',
      CANCELLED: 'default',
      TENTATIVE: 'warning'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/events`)}
          sx={{ mt: 2 }}
        >
          Back to Events
        </Button>
      </Box>
    );
  }

  if (!event) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Alert severity="warning">Event not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/events`)}
          sx={{ mt: 2 }}
        >
          Back to Events
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/events`)}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight={700}>
          Volunteers for {event.name}
        </Typography>
      </Box>

      {/* Event Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Date Range
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {new Date(event.event_start_date).toLocaleDateString()} - 
                {new Date(event.event_end_date).toLocaleDateString()}
              </Typography>
            </Grid>
            {event.event_start_time && event.event_end_time && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Daily Hours
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {event.event_start_time} - {event.event_end_time}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Location
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {event.location}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Type
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {event.event_type}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {statistics.total_assigned}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Assigned
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {statistics.available}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="error.main">
                  {statistics.not_available}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Not Available
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main">
                  {statistics.tentative}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tentative
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="text.secondary">
                  {statistics.cancelled}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cancelled
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4">
                  {statistics.required}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Required
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Volunteers Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Time Slots</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Responded</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {volunteers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No volunteers assigned yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                volunteers.map((ev) => (
                  <TableRow key={ev.id} hover>
                    <TableCell>{ev.volunteer?.full_name || '-'}</TableCell>
                    <TableCell>{ev.volunteer?.email || '-'}</TableCell>
                    <TableCell>{ev.volunteer?.phone_number || '-'}</TableCell>
                    <TableCell>
                      <Stack direction="column" spacing={0.5}>
                        {ev.time_slots && ev.time_slots.length > 0 ? (
                          ev.time_slots.map((slotAssignment) => (
                            <Chip
                              key={slotAssignment.id}
                              label={`${slotAssignment.time_slot?.slot_name || 'Unknown'} - ${new Date(slotAssignment.assigned_date).toLocaleDateString()}`}
                              size="small"
                              sx={{ mb: 0.5 }}
                              color={slotAssignment.availability_status === 'AVAILABLE' ? 'success' : 'default'}
                            />
                          ))
                        ) : (
                          <Chip 
                            label="Not Responded" 
                            size="small" 
                            color="default"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={AvailabilityStatusLabels[ev.availability_status] || ev.availability_status}
                        color={getStatusColor(ev.availability_status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {ev.responded_at
                        ? new Date(ev.responded_at).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {ev.response_notes || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default EventVolunteers;